import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Bell,
  Clock,
  AlertTriangle,
  Trophy,
  CheckCircle,
  CheckCheck,
  Mail,
  Settings,
  Calendar,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const REMINDER_OPTIONS = [
  { value: 7, label: '7 days before' },
  { value: 5, label: '5 days before' },
  { value: 3, label: '3 days before' },
  { value: 1, label: '1 day before' },
];

const getNotificationIcon = (type) => {
  switch (type) {
    case 'deadline':
      return <Clock className="w-5 h-5 text-amber-600" />;
    case 'conflict':
      return <AlertTriangle className="w-5 h-5 text-red-600" />;
    case 'recommendation':
      return <Trophy className="w-5 h-5 text-purple-600" />;
    case 'reminder':
      return <Calendar className="w-5 h-5 text-blue-600" />;
    default:
      return <Bell className="w-5 h-5 text-blue-600" />;
  }
};

const getNotificationColor = (type) => {
  switch (type) {
    case 'deadline':
      return 'bg-amber-50 border-amber-200';
    case 'conflict':
      return 'bg-red-50 border-red-200';
    case 'recommendation':
      return 'bg-purple-50 border-purple-200';
    case 'reminder':
      return 'bg-blue-50 border-blue-200';
    default:
      return 'bg-blue-50 border-blue-200';
  }
};

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('notifications');
  
  // Preferences state
  const [preferences, setPreferences] = useState({
    email_enabled: true,
    reminder_days: [7, 3, 1],
    notify_registered: true,
    notify_interested: true,
    notify_deadlines: true
  });
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll(filter === 'unread');
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await notificationsAPI.getPreferences();
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to load preferences');
    } finally {
      setPrefsLoading(false);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await notificationsAPI.markRead(notificationId);
      setNotifications(
        notifications.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(notifications.map((n) => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleReminderDayToggle = (day) => {
    setPreferences(prev => {
      const days = prev.reminder_days.includes(day)
        ? prev.reminder_days.filter(d => d !== day)
        : [...prev.reminder_days, day].sort((a, b) => b - a);
      return { ...prev, reminder_days: days };
    });
  };

  const handleSavePreferences = async () => {
    setSavingPrefs(true);
    try {
      await notificationsAPI.updatePreferences(preferences);
      toast.success('Notification preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  if (loading || prefsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Notifications</h1>
        <p className="text-slate-600 mt-1">
          Manage your notifications and email reminder preferences
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="notifications" className="flex items-center gap-2" data-testid="tab-notifications">
            <Bell className="w-4 h-4" />
            Notifications
            {unreadCount > 0 && (
              <Badge className="ml-1 bg-red-500 text-white text-xs">{unreadCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="tab-settings">
            <Settings className="w-4 h-4" />
            Email Settings
          </TabsTrigger>
        </TabsList>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6">
          {/* Filter Bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  filter === 'all'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="filter-all"
              >
                All
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  filter === 'unread'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid="filter-unread"
              >
                Unread
              </button>
            </div>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllRead}
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notifications List */}
          {notifications.length > 0 ? (
            <div className="space-y-3">
              {notifications.map((notification) => (
                <Card
                  key={notification.id}
                  className={`card-base ${
                    !notification.is_read ? getNotificationColor(notification.notification_type) : ''
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.notification_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-slate-900">{notification.title}</h4>
                          {!notification.is_read && (
                            <Badge className="bg-primary text-white text-xs">New</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{notification.message}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          {formatDate(notification.created_at)}
                        </p>
                      </div>
                      {!notification.is_read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkRead(notification.id)}
                          data-testid={`mark-read-${notification.id}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-base">
              <CardContent className="py-12 text-center">
                <Bell className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                </h3>
                <p className="text-slate-500">
                  {filter === 'unread'
                    ? "You're all caught up!"
                    : 'Notifications about tournaments and reminders will appear here'}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Email Settings Tab */}
        <TabsContent value="settings" className="mt-6">
          <div className="grid gap-6 max-w-2xl">
            {/* Email Toggle Card */}
            <Card className="card-base">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Email Notifications</CardTitle>
                      <CardDescription>Receive tournament reminders via email</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.email_enabled}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, email_enabled: checked })}
                    data-testid="email-toggle"
                  />
                </div>
              </CardHeader>
            </Card>

            {/* Reminder Days Card */}
            <Card className={`card-base ${!preferences.email_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Reminder Timing</CardTitle>
                    <CardDescription>Choose when to receive email reminders before tournaments</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {REMINDER_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        preferences.reminder_days.includes(option.value)
                          ? 'border-primary bg-primary/5'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleReminderDayToggle(option.value)}
                      data-testid={`reminder-${option.value}`}
                    >
                      <Checkbox
                        checked={preferences.reminder_days.includes(option.value)}
                        onCheckedChange={() => handleReminderDayToggle(option.value)}
                      />
                      <span className="text-sm font-medium">{option.label}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Notification Types Card */}
            <Card className={`card-base ${!preferences.email_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Bell className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">What to Notify</CardTitle>
                    <CardDescription>Select which tournament statuses should trigger reminders</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Registered Tournaments</p>
                      <p className="text-sm text-slate-500">Events you've registered for</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.notify_registered}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, notify_registered: checked })}
                    data-testid="notify-registered-toggle"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <Trophy className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Interested Tournaments</p>
                      <p className="text-sm text-slate-500">Events you're considering</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.notify_interested}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, notify_interested: checked })}
                    data-testid="notify-interested-toggle"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Registration Deadlines</p>
                      <p className="text-sm text-slate-500">Upcoming registration cutoffs</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.notify_deadlines}
                    onCheckedChange={(checked) => setPreferences({ ...preferences, notify_deadlines: checked })}
                    data-testid="notify-deadlines-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button
              className="btn-primary w-full md:w-auto"
              onClick={handleSavePreferences}
              disabled={savingPrefs}
              data-testid="save-preferences-btn"
            >
              {savingPrefs ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Preferences
                </>
              )}
            </Button>

            {/* Info Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">
                      Email reminders are sent daily at 8:00 AM UTC
                    </p>
                    <p className="text-sm text-blue-600 mt-1">
                      You'll receive reminders based on your selected timing (7, 5, 3, or 1 day before) 
                      for tournaments you're tracking or registered for.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
